import { useCallback, useSyncExternalStore } from 'react';

import { CONNECTIVITY_UI_OFFLINE_GRACE_MS } from '../../utils/connectivity-policy';

const getRawOnLineStatus = () =>
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? navigator.onLine
    : true;

let stableOnline = true;
let offlineTimer: ReturnType<typeof setTimeout> | null = null;
let listenersInitialized = false;

const listeners = new Set<() => void>();

function emitOnlineStatusChanged(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setStableOnline(next: boolean): void {
  if (stableOnline === next) return;
  stableOnline = next;
  emitOnlineStatusChanged();
}

function clearOfflineTimer(): void {
  if (!offlineTimer) return;
  clearTimeout(offlineTimer);
  offlineTimer = null;
}

function reconcileOnlineStatus(): void {
  if (getRawOnLineStatus()) {
    clearOfflineTimer();
    setStableOnline(true);
    return;
  }

  if (!stableOnline || offlineTimer) return;
  offlineTimer = setTimeout(() => {
    offlineTimer = null;
    if (!getRawOnLineStatus()) {
      setStableOnline(false);
    }
  }, CONNECTIVITY_UI_OFFLINE_GRACE_MS);
}

function initOnlineStatusListeners(): void {
  if (listenersInitialized || typeof window === 'undefined') return;
  listenersInitialized = true;
  window.addEventListener('online', reconcileOnlineStatus);
  window.addEventListener('offline', reconcileOnlineStatus);
}

/** Calls back when the stable online status changes (offline only after the
 * grace period). One window listener pair serves every subscriber. */
export function subscribeOnlineStatus(listener: () => void): () => void {
  initOnlineStatusListeners();
  reconcileOnlineStatus();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The stable online status that {@link subscribeOnlineStatus} reports. */
export function readSystemOnline(): boolean {
  return stableOnline;
}

export function useSystemOnline(): boolean {
  return useSyncExternalStore(
    useCallback(subscribeOnlineStatus, []),
    useCallback(readSystemOnline, []),
    useCallback(() => true, [])
  );
}
